// @ts-strict-ignore
import React, { ForwardedRef, forwardRef, memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  ButtonProps,
} from '@mui/material'
import ResponsiveDialog from 'components/ResponsiveDialog'
import ResponsiveButton from 'components/ResponsiveButton'
import Condition from 'components/Condition'
import { makeStyles } from 'tss-react/mui'

/**
 * Drop-in replacement for `@mui/material/Button` that shows a confirmation dialog before triggering the `onClick` handler.
 */
// TODO: add typings to props
// TODO: refactor this component to use our confirm dialog hooks and clean up the code

const useStyles = makeStyles()({
  criticalConfirm: {
    transition: 'color 0.4s ease',
    color: 'rgb(205, 0, 0) !important',
  },
  criticalConfirmDisabled: {
    transition: 'color 0.4s ease',
  },
})

export interface IConfirmButtonProps extends ButtonProps {
  critical?: boolean
  dialogTitle?: React.ReactNode
  dialogMessage?: React.ReactNode
  dialogConfirmLabel?: React.ReactNode
  dialogCancelLabel?: React.ReactNode
  icon?: React.ReactNode
  tooltip?: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  children?: React.ReactNode
  responsive?: boolean
  label?: React.ReactNode
  component?: React.ElementType
  onDialogClose?: () => void
  onDialogOpen?: () => void
  onCancel?: () => void
}

function ConfirmButton(props: IConfirmButtonProps, ref: ForwardedRef<HTMLButtonElement>) {
  const { classes } = useStyles()
  const {
    critical,
    dialogTitle = 'Are you sure?',
    dialogMessage = 'Are you sure?',
    dialogConfirmLabel = 'Yes',
    dialogCancelLabel = 'Cancel',
    icon,
    tooltip,
    onClick,
    children,
    responsive,
    label,
    component,
    onDialogClose,
    onDialogOpen,
    onCancel,
    ...otherProps
  } = props
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(!critical)
  const [confirmEnableTimeoutHandle, setConfirmEnableTimeoutHandle] = useState<number>(null)

  const clearButtonTimeout = useCallback(() => {
    if (confirmEnableTimeoutHandle) {
      clearTimeout(confirmEnableTimeoutHandle)
      setConfirmEnableTimeoutHandle(null)
    }
  }, [confirmEnableTimeoutHandle])

  const confirmButtonStyleName = useMemo(() => {
    if (critical) {
      if (isConfirmEnabled) {
        return classes.criticalConfirm
      }
      return classes.criticalConfirmDisabled
    }
    return ''
  }, [critical, isConfirmEnabled])

  const handleConfirmClick = useCallback((e) => {
    if (!isConfirmEnabled) {
      // extra safeguard for any possible rogue events coming up
      return
    }

    clearButtonTimeout()

    setDialogOpen(false)
    setIsConfirmEnabled(false)

    if (onDialogClose) {
      onDialogClose()
    }

    if (onClick) {
      onClick(e)
    }
  }, [onClick, onDialogClose, clearButtonTimeout, confirmEnableTimeoutHandle, isConfirmEnabled])

  const handleCancelClick = useCallback(() => {
    clearButtonTimeout()

    setDialogOpen(false)
    setIsConfirmEnabled(false)

    if (onCancel) {
      onCancel()
    }

    if (onDialogClose) {
      onDialogClose()
    }
  }, [onCancel, onDialogClose, clearButtonTimeout, confirmEnableTimeoutHandle])

  const handleDialogClose = useCallback(() => {
    clearButtonTimeout()

    setDialogOpen(false)
    setIsConfirmEnabled(false)

    if (onCancel) {
      onCancel()
    }

    if (onDialogClose) {
      onDialogClose()
    }
  }, [onCancel, onDialogClose, confirmEnableTimeoutHandle, clearButtonTimeout, confirmEnableTimeoutHandle])

  const handleButtonClick = useCallback((e) => {
    e.stopPropagation()

    if (onDialogOpen) {
      onDialogOpen()
    }

    setDialogOpen(true)

    if (critical) {
      clearButtonTimeout()
      const timeoutHandle = window.setTimeout(() => {
        setIsConfirmEnabled(true)
      }, 800)

      setConfirmEnableTimeoutHandle(timeoutHandle)
    } else {
      setIsConfirmEnabled(true)
    }
  }, [onDialogOpen, critical, confirmEnableTimeoutHandle, clearButtonTimeout, confirmEnableTimeoutHandle])

  useEffect(() => {
    return () => {
      if (confirmEnableTimeoutHandle) {
        clearTimeout(confirmEnableTimeoutHandle)
      }
    }
  }, [confirmEnableTimeoutHandle])

  const buttonProps = useMemo(() => ({
    ...otherProps,
    onClick: handleButtonClick,
  }), [otherProps, handleButtonClick])

  let buttonElement = (
    <Button ref={ref} {...buttonProps}>
      {children}
    </Button>
  )

  if (component) {
    const ButtonComponent = component

    buttonElement = (
      <ButtonComponent ref={ref} {...buttonProps} responsive={responsive}>
        {children}
      </ButtonComponent>
    )
  } else if (responsive) {
    buttonElement = (
      <ResponsiveButton
        ref={ref}
        icon={icon}
        tooltip={tooltip}
        label={label}
        {...buttonProps}
      >
        {children}
      </ResponsiveButton>
    )
  } else if (icon) {
    if (tooltip) {
      buttonElement = (
        <Tooltip title={tooltip}>
          <IconButton ref={ref} {...buttonProps}>
            {children}
          </IconButton>
        </Tooltip>
      )
    } else {
      buttonElement = (
        <IconButton ref={ref} {...buttonProps}>
          {children}
        </IconButton>
      )
    }
  }

  return (
    <React.Fragment>
      {buttonElement}

      {isDialogOpen && (
        <ResponsiveDialog
          open
          onClose={handleDialogClose}
          onClick={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          data-cy="confirm-dialog"
        >
          <Condition condition={Boolean(dialogTitle)}>
            <DialogTitle data-cy="confirm-dialog-title">
              {dialogTitle}
            </DialogTitle>
          </Condition>

          <Condition condition={Boolean(dialogMessage)}>
            <DialogContent>
              <DialogContentText data-cy="confirm-dialog-message">
                {dialogMessage}
              </DialogContentText>
            </DialogContent>
          </Condition>

          <DialogActions>
            <Button
              onClick={handleCancelClick}
              data-cy="confirm-dialog-cancel"
            >
              {dialogCancelLabel}
            </Button>

            <Button
              color="primary"
              onClick={handleConfirmClick}
              disabled={!isConfirmEnabled}
              data-cy="confirm-dialog-confirm"
            >
              <span className={confirmButtonStyleName}>
                {dialogConfirmLabel}
              </span>
            </Button>
          </DialogActions>
        </ResponsiveDialog>
      )}
    </React.Fragment>
  )
}

export default memo(forwardRef(ConfirmButton))
